import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, HeadphonesIcon, MapPinned, Sparkles, MessageCircle, Star } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { ResponsiveImg } from "@/components/site/ResponsiveImg";
import { WIDTHS } from "@/lib/image";
import { useDestinations, useTestimonials } from "@/lib/content";
import { BLOGS } from "@/data/blogs";
import { SITE, waLink, waMessageFor } from "@/config/site";
import { trackWhatsappClick } from "@/lib/analytics";

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

const TRUST = [
  { icon: Compass, title: "Curated Itineraries", body: "Each trip is designed from scratch by travelers who've walked the route." },
  { icon: MapPinned, title: "Local Experts", body: "On-ground partners in Andaman, Goa, Rajasthan and Ladakh — not a call centre." },
  { icon: HeadphonesIcon, title: "24×7 On-Trip Support", body: "A dedicated coordinator on WhatsApp for the entire duration of your trip." },
  { icon: Sparkles, title: "Fully Customizable", body: "Every package on this site is a starting point. We shape it around you." },
];

const HeroSection = () => (
  <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
    <ResponsiveImg
      src="https://images.unsplash.com/photo-1663407978077-ca116e36abf0?w=1920&q=65&auto=format&fit=crop"
      widths={WIDTHS.hero}
      alt="Motorcycle overlooking a dramatic Ladakh mountain road at high altitude"
      className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
      loading="eager"
      fetchPriority="high"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-ocean-deep/70 via-ocean/60 to-ocean-deep" />
    <div className="absolute inset-0 grain" />
    <div className="relative z-10 container-x h-full flex flex-col justify-end pb-16 md:pb-24">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="overline text-white/80"
      >
        Adventure - first travel · India
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="mt-4 font-heading text-white text-[2.75rem] leading-[1.05] md:text-7xl lg:text-[5.5rem] tracking-tighter [word-spacing:0.2em] font-semibold max-w-5xl uppercase"
      >
        Travel that leaves a mark.
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.25 }}
        className="mt-6 max-w-2xl text-white/80 text-lg md:text-xl leading-relaxed"
      >
        We build adventure-forward, deeply personalised trips across Andaman, Goa, Rajasthan and
        Leh-Ladakh — for families, couples, groups and solo travelers.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="mt-10 flex flex-wrap gap-4"
      >
        <Link to="/destinations" data-testid="hero-explore-cta" className="btn-primary">
          Explore Destinations <ArrowRight size={18} />
        </Link>
        <a
          href={waLink(waMessageFor())}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-whatsapp-cta"
          onClick={() => trackWhatsappClick("home-hero")}
          className="btn-secondary"
        >
          <MessageCircle size={18} /> Plan on WhatsApp
        </a>
      </motion.div>
    </div>
  </section>
);

const TrustStrip = () => (
  <section className="section bg-sand-50">
    <div className="container-x">
      <p className="overline text-center">Why travel with us</p>
      <h2 className="mt-3 text-center font-heading text-4xl md:text-5xl text-ocean tracking-tight max-w-3xl mx-auto">
        A small team, obsessed with getting your trip exactly right.
      </h2>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {TRUST.map(({ icon: Icon, title, body }, idx) => (
          <div
            key={title}
            data-testid={`trust-pillar-${idx}`}
            className="p-8 bg-white border border-sand-200 hover:border-sunset transition-colors"
          >
            <Icon size={28} strokeWidth={1.5} className="text-sunset mb-6" />
            <h3 className="font-heading text-xl text-ocean mb-2">{title}</h3>
            <p className="text-sm text-ink-muted leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DestinationsGrid = () => {
  const { data: DESTINATIONS, isLoading } = useDestinations();
  if (isLoading) return null;

  return (
  <section className="section bg-white" id="destinations">
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <p className="overline">Where we travel</p>
          <h2 className="mt-3 font-heading text-4xl md:text-5xl text-ocean max-w-2xl">
            Four destinations. Endless ways to travel them.
          </h2>
        </div>
        <Link to="/destinations" className="text-sunset font-semibold uppercase tracking-widest text-sm hover:underline" data-testid="destinations-see-all">
          See all →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {DESTINATIONS.map((d, i) => (
          <Link
            key={d.slug}
            to={d.path}
            data-testid={`destination-card-${d.slug}`}
            className="group relative aspect-[3/4] overflow-hidden bg-ocean"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <ResponsiveImg
              src={d.cardImage}
              widths={WIDTHS.card}
              sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
              alt={d.cardAlt}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ocean-deep via-ocean/40 to-transparent" />
            <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
              <p className="overline text-white/70">From {inr(d.startingFrom)}</p>
              <h3 className="mt-2 font-heading text-3xl">{d.name}</h3>
              <p className="mt-2 text-sm text-white/80">{d.hook}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-sunset text-xs font-semibold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                Explore <ArrowRight size={14} />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

const FeaturedPackages = () => {
  const { data: DESTINATIONS, isLoading } = useDestinations();
  if (isLoading) return null;

  const featured = DESTINATIONS.flatMap((d) =>
    d.packages.slice(0, 1).map((p) => ({ ...p, destName: d.shortName, destPath: d.path, image: d.heroImage }))
  );
  return (
    <section className="section bg-ocean text-white relative overflow-hidden">
      <div className="container-x relative z-10">
        <div className="max-w-2xl mb-14">
          <p className="overline">Signature packages</p>
          <h2 className="mt-3 font-heading text-4xl md:text-5xl">Handpicked trips, ready to go.</h2>
          <p className="mt-4 text-white/70">
            Four of our most-loved itineraries. Everything is customisable — treat these as a strong starting point.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p) => (
            <Link
              key={p.id}
              to={p.destPath}
              data-testid={`featured-package-${p.id}`}
              className="group bg-ocean-light hover:bg-ocean-light/70 transition-colors border border-white/10 flex flex-col"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <ResponsiveImg
                  src={p.image}
                  widths={WIDTHS.card}
                  sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="overline text-white/60">{p.destName} · {p.duration}</p>
                <h3 className="mt-2 font-heading text-xl">{p.name}</h3>
                <ul className="mt-4 space-y-1 text-sm text-white/70">
                  {p.highlights.slice(0, 3).map((h) => (
                    <li key={h} className="flex gap-2"><span className="text-sunset">·</span>{h}</li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                  <span className="text-sm text-white/60">From</span>
                  <span className="font-heading text-xl text-sunset">{inr(p.startingFrom)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { data: TESTIMONIALS, isLoading } = useTestimonials();
  if (isLoading) return null;

  return (
  <section className="section bg-sand-100">
    <div className="container-x">
      <p className="overline text-center">Traveler stories</p>
      <h2 className="mt-3 text-center font-heading text-4xl md:text-5xl text-ocean max-w-3xl mx-auto">
        Real trips. Real people. No embellishment.
      </h2>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <div key={t.name} data-testid={`testimonial-${i}`} className="bg-white border border-sand-200 p-8">
            <div className="flex gap-1 text-sunset mb-4">
              {Array.from({ length: t.rating }).map((_, k) => (
                <Star key={k} size={16} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <p className="font-serif text-xl text-ocean italic leading-relaxed">"{t.quote}"</p>
            <div className="mt-6 pt-6 border-t border-sand-200">
              <p className="font-semibold text-ocean">{t.name}</p>
              <p className="text-sm text-ink-muted">{t.location}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const LatestBlogs = () => (
  <section className="section bg-white">
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <p className="overline">Journal</p>
          <h2 className="mt-3 font-heading text-4xl md:text-5xl text-ocean">Notes from the road.</h2>
        </div>
        <Link to="/blog" className="text-sunset font-semibold uppercase tracking-widest text-sm hover:underline">
          All journal entries →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {BLOGS.slice(0, 3).map((post) => (
          <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`home-blog-${post.slug}`} className="group">
            <div className="aspect-[4/3] overflow-hidden bg-ocean">
              <ResponsiveImg
                src={post.image}
                widths={WIDTHS.card}
                sizes="(min-width: 768px) 33vw, 100vw"
                alt={post.imageAlt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="pt-6">
              <p className="overline text-ink-muted">{post.category} · {post.readingTime}</p>
              <h3 className="mt-2 font-heading text-2xl text-ocean group-hover:text-sunset transition-colors">{post.title}</h3>
              <p className="mt-3 text-sm text-ink-muted line-clamp-2">{post.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const HomeEnquiry = () => (
  <section className="section bg-ocean-light relative">
    <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div className="text-white">
        <p className="overline">Plan your trip</p>
        <h2 className="mt-3 font-heading text-4xl md:text-5xl leading-tight">
          Not sure yet? Send us the rough idea. We'll do the rest.
        </h2>
        <p className="mt-6 text-white/70 max-w-lg">
          We reply on WhatsApp with a custom itinerary and a quote — usually within a few working hours.
          No online payments; we invoice you only after your itinerary is confirmed.
        </p>
        <ul className="mt-8 space-y-3 text-white/75">
          <li className="flex gap-3"><span className="text-sunset">→</span> Free consultation & itinerary drafting</li>
          <li className="flex gap-3"><span className="text-sunset">→</span> Transparent quote with inclusions & exclusions</li>
          <li className="flex gap-3"><span className="text-sunset">→</span> 24×7 on-trip WhatsApp coordinator</li>
        </ul>
      </div>
      <EnquiryForm dark sourcePage="home-hero" />
    </div>
  </section>
);

export default function HomePage() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE.name,
      url: SITE.domain,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE.domain}/blog?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];
  return (
    <>
      <SEO
        title="Deep Dive Trips | Andaman, Goa, Rajasthan & Ladakh Tour Packages"
        description="Adventure-first travel company crafting curated and customizable tour packages for Andaman, Goa, Rajasthan and Leh-Ladakh. Plan your trip on WhatsApp today."
        path="/"
        jsonLd={jsonLd}
      />
      <HeroSection />
      <TrustStrip />
      <DestinationsGrid />
      <FeaturedPackages />
      <Testimonials />
      <LatestBlogs />
      <HomeEnquiry />
    </>
  );
}
